// src/components/editable/AddFieldButton.tsx
// Button + popover to add a new field to a record

import React, { useState } from "react";
import {
  Chip,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Divider,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { FieldInfo } from "hooks/useFieldSchema";
import { useTranslation } from "../../i18n";

interface AddFieldButtonProps {
  /** All available fields from schema */
  availableFields: Record<string, FieldInfo>;
  /** Fields already shown/used (to exclude from selection) */
  usedFields: string[];
  /** Called when user selects a field to add */
  onAddField: (fieldName: string) => void;
  /** Button size */
  size?: "small" | "medium";
  /** Tooltip text */
  tooltip?: string;
  /** Chip label text (defaults to "Add field") */
  label?: string;
  /** "onDark" for royal-blue / dark headers — uses white-based colors */
  variant?: "default" | "onDark";
}

const AddFieldButton: React.FC<AddFieldButtonProps> = ({
  availableFields,
  usedFields,
  onAddField,
  size = "small",
  tooltip,
  label,
  variant = "default",
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  const chipLabel = label || t("form.add_field", "Add field");
  const tooltipText = tooltip || chipLabel;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setSearchTerm("");
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  const handleSelectField = (fieldName: string) => {
    onAddField(fieldName);
    handleClose();
  };

  // Get fields that can be added (not already used)
  const addableFields = Object.entries(availableFields)
    .filter(([name]) => !usedFields.includes(name))
    .filter(([name, info]) => {
      // Filter by search term
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        name.toLowerCase().includes(searchLower) ||
        (info.verbose_name?.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => {
      // Sort by verbose_name or field name
      const nameA = a[1].verbose_name || a[0];
      const nameB = b[1].verbose_name || b[0];
      return nameA.localeCompare(nameB);
    });

  const open = Boolean(anchorEl);

  if (Object.keys(availableFields).length === 0) {
    return null;
  }

  return (
    <>
      <Chip
        icon={<AddIcon sx={{ fontSize: 14 }} />}
        label={chipLabel}
        size="small"
        title={tooltipText}
        onClick={handleClick}
        sx={{
          height: 24,
          fontSize: "0.72rem",
          fontWeight: 500,
          cursor: "pointer",
          backgroundColor: variant === "onDark"
            ? alpha(theme.palette.common.white, 0.12)
            : alpha(theme.palette.info.main, 0.1),
          color: variant === "onDark"
            ? alpha(theme.palette.common.white, 0.85)
            : theme.palette.info.main,
          border: variant === "onDark"
            ? `1px solid ${alpha(theme.palette.common.white, 0.2)}`
            : "1px solid transparent",
          backdropFilter: "blur(8px)",
          transition: "all 0.25s ease",
          "& .MuiChip-icon": {
            color: variant === "onDark"
              ? alpha(theme.palette.common.white, 0.85)
              : theme.palette.info.main,
          },
          "&:hover": {
            backgroundColor: variant === "onDark"
              ? alpha(theme.palette.common.white, 0.22)
              : alpha(theme.palette.primary.main, 0.18),
            color: variant === "onDark"
              ? theme.palette.common.white
              : theme.palette.primary.main,
            borderColor: variant === "onDark"
              ? alpha(theme.palette.common.white, 0.4)
              : alpha(theme.palette.primary.main, 0.4),
            "& .MuiChip-icon": {
              color: variant === "onDark"
                ? theme.palette.common.white
                : theme.palette.primary.main,
            },
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { width: 280, maxHeight: 480 },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ px: 1, py: 0.5 }}>
            Select field to add
          </Typography>
          <TextField
            size="small"
            placeholder={t("form.search_fields", "Search fields...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
        </Box>
        <Divider />
        <List
          dense
          onWheelCapture={(e) => e.stopPropagation()}
          sx={{ maxHeight: 380, overflow: "auto", py: 0 }}
        >
          {addableFields.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 2, textAlign: "center" }}
            >
              {searchTerm ? "No matching fields" : "All fields already added"}
            </Typography>
          ) : (
            addableFields.map(([fieldName, fieldInfo]) => (
              <ListItemButton
                key={fieldName}
                onClick={() => handleSelectField(fieldName)}
                sx={{ py: 0.25, minHeight: 28 }}
              >
                <ListItemText
                  primary={fieldInfo.verbose_name || fieldName.replace(/_/g, " ")}
                  secondary={fieldInfo.help_text?.slice(0, 60) + (fieldInfo.help_text && fieldInfo.help_text.length > 60 ? "..." : "")}
                  primaryTypographyProps={{ variant: "body2", sx: { fontSize: "0.8rem" } }}
                  secondaryTypographyProps={{ variant: "caption", noWrap: true, sx: { fontSize: "0.7rem" } }}
                />
              </ListItemButton>
            ))
          )}
        </List>
      </Popover>
    </>
  );
};

export default AddFieldButton;
